"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";

const TESTIMONIALS = [
  {
    quote:
      "Core HR functions are now centralized and accessible, cutting our admin burden dramatically. Our HR team finally has time for strategic work instead of paperwork.",
    name: "Priya Sharma",
    role: "Director of People Ops",
    company: "Nexora",
    rating: 5,
  },
  {
    quote:
      "Payroll used to take us three days every month. Now it's an afternoon, and I trust the numbers without double-checking everything by hand.",
    name: "Arjun Mehta",
    role: "HR Generalist",
    company: "Brightfield",
    rating: 5,
  },
  {
    quote:
      "AeronPay gave our managers back the time they were spending chasing leave approvals over email and Slack.",
    name: "Kavya Reddy",
    role: "HR Director",
    company: "Meridian",
    rating: 5,
  },
];

const OVERALL_STATS = [
  { value: "4.8/5", label: "Average rating" },
  { value: "200+", label: "Companies onboarded" },
  { value: "98%", label: "Would recommend" },
];

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const next = () => setIndex((i) => (i + 1) % TESTIMONIALS.length);
  const prev = () =>
    setIndex((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const t = TESTIMONIALS[index];

  return (
    <section className="py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-xl mx-auto mb-14">
          <span className="text-sm font-semibold text-primary">
            Customer stories
          </span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mt-3">
            Hear from our customers
          </h2>
        </div>

        <div className="grid md:grid-cols-[1fr_2fr] gap-8 items-stretch">
          <div className="bg-card border border-border rounded-2xl p-8 flex flex-col justify-center gap-8">
            {OVERALL_STATS.map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-bold text-foreground">
                  {s.value}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          <div className="relative bg-card border border-border rounded-2xl p-10 flex flex-col justify-between overflow-hidden">
            <Quote
              size={64}
              className="absolute -top-3 -left-3 text-primary/5"
              strokeWidth={1}
            />

            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.35 }}
                className="relative"
              >
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className="fill-warning text-warning"
                    />
                  ))}
                </div>
                <p className="text-xl text-foreground leading-relaxed mb-8">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                    {t.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {t.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t.role}, {t.company}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="relative flex items-center justify-between mt-8 pt-6 border-t border-border">
              <div className="flex gap-2">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-primary" : "w-1.5 bg-border"}`}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={prev}
                  className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={next}
                  className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
